import { useState } from 'react';
import { api, can, getUser } from '../lib/api';
import { bytes, dateTime, localZoneLabel } from '../lib/format';
import {
  Card, Empty, ErrorNote, Field, Hash, Loading, Modal, PageHead, Pill, useResource, useToast,
} from '../components/ui';

/**
 * CM-8 Evidence export (wireframe W8).
 *
 * One click from complaint to defence file. The manifest hash is the point of
 * the screen: record it when the export is made, and a stored bundle can be
 * proven unmodified years later.
 */

interface ExportJob {
  id: string;
  scope: { kind: string; principalHash?: string; purposeId?: string; from?: string; to?: string };
  status: 'queued' | 'running' | 'ready' | 'error';
  requested_by: string;
  manifest_hash: string | null;
  key_id: string | null;
  byte_size: number | null;
  created_at: string;
  finished_at: string | null;
}

export default function Exports({ embedded = false }: { embedded?: boolean } = {}) {
  const writable = can(getUser(), 'ledger:export');
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [nonce, setNonce] = useState(0);

  const jobs = useResource<{ exports: ExportJob[] }>(() => api.get('/v1/exports'), [nonce]);
  const key = useResource<{ keyId: string; publicKeyPem: string }>(() => api.get('/v1/exports/public-key'));

  return (
    <>
      {embedded
        ? <h2 className="section" style={{ display: 'flex', alignItems: 'center', gap: 12 }}><span>Signed exports</span>{writable && <button className="small" style={{ marginInlineStart: 'auto' }} onClick={() => setCreating(true)}>New export</button>}</h2>
        : (
          <PageHead eyebrow="Consent ledger" backTo="/ledger" title="Signed exports">
            {writable && <button className="primary" onClick={() => setCreating(true)}>New export</button>}
          </PageHead>
        )}

      <ErrorNote error={jobs.error} />

      <div className="note" style={{ marginBottom: 14 }}>
        Exports are signed with Ed25519 key{' '}
        {key.data && <span className="mono">{key.data.keyId}</span>}. The public key is included
        in every export.
      </div>

      {jobs.loading && !jobs.data && <Loading what="Loading exports" />}

      {jobs.data && (jobs.data.exports.length === 0 ? (
        <Empty>No exports.</Empty>
      ) : (
        <Card>
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Export</th>
                  <th>Scope</th>
                  <th>Generated</th>
                  <th>Requested by</th>
                  <th>Manifest hash</th>
                  <th className="num">Size</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {jobs.data.exports.map((j) => (
                  <tr key={j.id}>
                    <td>{j.scope.kind === 'principal' ? 'One person' : j.scope.kind === 'purpose' ? 'One purpose' : 'Whole record'}</td>
                    <td className="small">
                      {j.scope.principalHash && <div className="muted" style={{ marginTop: 4 }}><Hash value={j.scope.principalHash} /></div>}
                      {(j.scope.from || j.scope.to) && (
                        <div className="muted">{j.scope.from?.slice(0, 10) ?? '…'} → {j.scope.to?.slice(0, 10) ?? 'today'}</div>
                      )}
                    </td>
                    <td className="small mono">{dateTime(j.finished_at ?? j.created_at)}</td>
                    <td className="small">{j.requested_by}</td>
                    <td><Hash value={j.manifest_hash} head={12} /></td>
                    <td className="num small">{bytes(j.byte_size)}</td>
                    <td>
                      {j.status === 'ready' ? (
                        <button
                          className="small"
                          onClick={() =>
                            api.download(`/v1/exports/${j.id}/download`, `${j.id}.zip`)
                              .catch((err) => toast((err as Error).message, 'bad'))
                          }
                        >
                          Download
                        </button>
                      ) : (
                        <Pill tone={j.status === 'error' ? 'bad' : 'warn'}>{j.status}</Pill>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      {creating && (
        <NewExport
          onClose={() => setCreating(false)}
          onDone={(hash) => {
            setCreating(false);
            setNonce((n) => n + 1);
            toast(`Export ready. Record manifest hash ${hash.slice(0, 16)}… with the file.`, 'ok');
          }}
        />
      )}
    </>
  );
}

function NewExport({ onClose, onDone }: { onClose: () => void; onDone: (manifestHash: string) => void }) {
  const [kind, setKind] = useState<'principal' | 'purpose' | 'tenant'>('principal');
  const [principalHash, setPrincipalHash] = useState('');
  const [purposeId, setPurposeId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [includeAuditLog, setIncludeAuditLog] = useState(true);
  const [includePropagation, setIncludePropagation] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const purposes = useResource<{ purposes: Array<{ id: string; current: { name: string } }> }>(
    () => api.get('/v1/purposes'),
  );

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ export: { manifestHash: string } }>('/v1/exports', {
        kind,
        principalHash: kind === 'principal' ? principalHash.trim() : undefined,
        purposeId: kind === 'purpose' ? purposeId : undefined,
        from: from || undefined,
        to: to || undefined,
        includeAuditLog,
        includePropagation,
      });
      onDone(res.export.manifestHash);
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New export"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button
            className="primary" onClick={generate}
            disabled={busy || (kind === 'principal' && !principalHash) || (kind === 'purpose' && !purposeId)}
          >
            {busy ? 'Generating…' : 'Generate export'}
          </button>
        </>
      }
    >
      <div className="field">
        <label>Scope</label>
        {([
          ['principal', 'Data principal', 'All records for one data principal: notices shown, decisions and timestamps.'],
          ['purpose', 'Purpose', 'All records collected against one purpose.'],
          ['tenant', 'Full deployment', 'All records.'],
        ] as const).map(([value, title, help]) => (
          <label key={value} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '6px 0', fontWeight: 400 }}>
            <input
              type="radio" name="scope" checked={kind === value} style={{ width: 'auto', marginTop: 3 }}
              onChange={() => setKind(value)}
            />
            <span>
              <strong>{title}</strong>
              <div className="help">{help}</div>
            </span>
          </label>
        ))}
      </div>

      {kind === 'principal' && (
        <Field label="Principal hash" help="Available from the ledger. Raw identifiers are not accepted.">
          <input type="text" value={principalHash} onChange={(e) => setPrincipalHash(e.target.value)} />
        </Field>
      )}

      {kind === 'purpose' && (
        <Field label="Purpose">
          <select value={purposeId} onChange={(e) => setPurposeId(e.target.value)}>
            <option value="">Select purpose</option>
            {purposes.data?.purposes.map((p) => <option key={p.id} value={p.id}>{p.current.name}</option>)}
          </select>
        </Field>
      )}

      <div className="row">
        <Field label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      </div>

      <div className="field">
        <label>Include</label>
        <label style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '4px 0', fontWeight: 400 }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={includePropagation} onChange={(e) => setIncludePropagation(e.target.checked)} />
          Propagation delivery trail (S.6(6))
        </label>
        <label style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '4px 0', fontWeight: 400 }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={includeAuditLog} onChange={(e) => setIncludeAuditLog(e.target.checked)} />
          Audit log for the period
        </label>
        <div className="help">
          Always included: event stream, notice versions and hashes, chain verification, public
          key, PDF summary.
        </div>
      </div>

      <ErrorNote error={error} />
    </Modal>
  );
}
